import { type SugarEnrollmentItem } from '@app/types'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { compact, deburr, map } from 'lodash'

import { rosterScholarshipAward, rosterTuitionCredit } from './funding'
import { type RosterOptionLabel } from './rosterSort'

dayjs.extend(localizedFormat)

// Blank rather than the raw wire token for an absent value, and the token itself
// when Sugar's option list has no label for it, so a picklist value added after
// this ships lands in the file instead of silently blanking a cell.
//
// That fallback is NOT exact screen parity, for two of the four columns it serves.
// The Application Status and Scholarship badges fall back the same way, but the
// Participation Type cell and EnrollmentStatusBadge render the label alone and show
// nothing for a value the option list does not cover -- so an unmapped value reads
// as a token here and as blank on the roster. Kept deliberately: an export that
// drops a value it is holding is worse than one that prints a token, and the file
// is read without the row beside it. The renderers are the side worth changing, and
// that is a UI change rather than this ticket's.
const csvOption = (
  getOptionLabel: RosterOptionLabel,
  option: Parameters<RosterOptionLabel>[0],
  value: string | null | undefined
): string => (value ? getOptionLabel(option, value) ?? value : '')

// 'LL' matches every other date the portal shows a provider (EnrollmentViewModal,
// the documents tables), so a value read off the screen and a value read out of
// the file are the same string.
const csvDate = (value: string | null | undefined): string => (value ? dayjs(value).format('LL') : '')

type RosterCsvColumn = {
  header: string
  value: (item: SugarEnrollmentItem, getOptionLabel: RosterOptionLabel) => string
}

// Order is the ticket's (MIQ-2688), not the table's -- this is the list Erin
// asked for, and the roster's own column order differs.
const ROSTER_CSV_COLUMNS: readonly RosterCsvColumn[] = [
  // MIQ-2830: the roster payload carries only the combined "Last, First"
  // string, so both of these are blank until it exposes the halves. Splitting
  // `contact.name` on the comma here was rejected -- a suffix ("Smith, Jr.")
  // puts the wrong text in both columns and nothing about the output says so.
  { header: 'First Name', value: ({ contact }) => contact.firstName ?? '' },
  { header: 'Last Name', value: ({ contact }) => contact.lastName ?? '' },
  {
    header: 'Application Status',
    value: ({ application }, getOptionLabel) => csvOption(getOptionLabel, 'applicationStatus', application.status)
  },
  {
    header: 'Application Status Details',
    value: ({ application }, getOptionLabel) =>
      csvOption(getOptionLabel, 'applicationStatusDetails', application.statusDetails)
  },
  { header: 'Application Approval Date', value: ({ application }) => csvDate(application.approvalDate) },
  {
    header: 'Participation Type',
    value: ({ enrollment }, getOptionLabel) =>
      csvOption(getOptionLabel, 'participationTypes', enrollment.participationType)
  },
  {
    header: 'Enrollment Status',
    value: ({ enrollment }, getOptionLabel) => csvOption(getOptionLabel, 'enrollmentStatus', enrollment.status)
  },
  // Through rosterTuitionCredit rather than the raw field: MIQ-2794 shows this
  // only on an approved application whose sub-status says the student is taking
  // the credit, and suppresses a zero. Reading enrollment.tuitionCredit directly
  // exported a credit for denied and pending rows that the roster leaves blank --
  // $508.00 against a denied, inactive student in the first live export.
  { header: 'Tuition Credit Amount', value: (item) => rosterTuitionCredit(item) ?? '' },
  {
    header: 'DPP Scholarship Status',
    value: ({ enrollment }, getOptionLabel) => csvOption(getOptionLabel, 'scholarshipStatus', enrollment.gapStatus)
  },
  // No entry in SugarOptionName for this one, and ScholarshipSubStatusBadge
  // renders it raw -- so raw is what matches the screen.
  { header: 'DPP Scholarship Sub-Status', value: ({ enrollment }) => enrollment.gapScholarshipSubStatus ?? '' },
  // Blank unless the scholarship is active, by the same predicate the roster's
  // Scholarship column uses. This column originally exported the award at every
  // status; the program lead reversed that on 2026-09-16 (MIQ-2688), so a pending
  // or rescinded student's figure no longer appears here, just as it does not on
  // screen. Status and sub-status are unaffected.
  { header: 'DPP Scholarship Award Amount', value: (item) => rosterScholarshipAward(item) ?? '' },
  { header: 'Enrollment Start Date', value: ({ enrollment }) => csvDate(enrollment.startDate) }
]

// Passed to the formatter explicitly rather than inferred from the first row:
// an empty roster has no first row, and inference would produce a zero-byte
// file with no header line at all.
export const ROSTER_CSV_HEADERS: string[] = map(ROSTER_CSV_COLUMNS, 'header')

// fast-csv defaults rowDelimiter to a bare LF. RFC 4180 says CRLF, and the
// consumer here is Excel on Windows, so the delimiter is stated rather than
// inherited -- it is one option, and being wrong about it is invisible until a
// provider opens the file.
export const ROSTER_CSV_FORMAT_OPTIONS = {
  // Without this a site with no students exports the byte order mark and
  // nothing else -- not even the column names. Stating `headers` is not enough
  // on its own; fast-csv writes them lazily with the first row, and there is no
  // first row. A provider opening that file cannot tell it from a broken one.
  alwaysWriteHeaders: true,
  headers: ROSTER_CSV_HEADERS,
  rowDelimiter: '\r\n'
}

// CSV formula injection. Excel, LibreOffice and Google Sheets all evaluate a cell
// whose text begins with one of these, so a roster value starting with `=` becomes
// a formula the moment a provider opens the download -- and the roster carries
// family-supplied text, which is the definition of a trust boundary. Quoting does
// not help; the spreadsheet evaluates the contents either way. A leading apostrophe
// is the documented mitigation and all three read it as "this is text" and hide it.
//
// Leading whitespace is skipped first because a spreadsheet strips it on import,
// exposing whatever follows it to the same evaluation. `\s` rather than a
// hand-picked list: the rule is about leading whitespace, and the list this
// replaced had tab, carriage return and line feed but not the space itself
// (MIQ-2950). Whitespace followed by ordinary text is left alone.
//
// fast-csv has no formula escaping of its own -- its `escape` option is the quote
// escape character, which is a different problem.
const FORMULA_LEAD = /^\s*[=+\-@]/

// A negative currency would also be prefixed. Awards and credits are never negative
// here, and an apostrophe on one would be cosmetic rather than wrong.
const csvSafe = (value: string): string => (FORMULA_LEAD.test(value) ? `'${value}` : value)

// Every column is funnelled through csvSafe here rather than in the individual
// value functions: a column added later is then covered without its author having
// to know this hazard exists.
export const toRosterCsvRows = (items: SugarEnrollmentItem[], getOptionLabel: RosterOptionLabel): string[][] =>
  map(items, (item) => map(ROSTER_CSV_COLUMNS, ({ value }) => csvSafe(value(item, getOptionLabel))))

const slugify = (value: string): string =>
  deburr(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

// compact() drops the empty segments rather than leaving "roster---20260907":
// site and program year are resolved from /me and either can be missing.
export const rosterCsvFilename = (siteName?: string, programYearName?: string): string =>
  `${compact(['roster', slugify(siteName ?? ''), slugify(programYearName ?? ''), dayjs().format('YYYYMMDD')]).join(
    '-'
  )}.csv`
